import { Typography, Grid, Button, LinearProgress, AppBar, Dialog, IconButton, Toolbar, CircularProgress } from "@mui/material";
import { axisClasses } from '@mui/x-charts';
import { BarChart } from '@mui/x-charts/BarChart';
import { useEffect, useState } from "react";
import { auth } from "../firebase/firebase";
import { argosApiEntryPoint } from "../GlobalConfig";
import { DrillDownParams } from "./CommonInterfaces";
import { Item, percentage, round } from "./CommonComponents";
import InstitutionSummaryJournalGraph from "./InstitutionSummaryJournalGraph";
import InstitutionSummaryPublisherGraph from "./InstitutionSummaryPublisherGraph";
import DrillDownTable from "./DrillDownTable";
import CloseIcon from '@mui/icons-material/Close';
import CollaboratingInstitutionsTable from "./institutional-summaries/CollaboratingInstitutionsTable";
import MostFrequentCollaboratorsTable from "./institutional-summaries/MostFrequentCollaboratorsTable";
import MostRetractedAuthorsTable from "./institutional-summaries/MostRetractedAuthorsTable";
import RetractionsForOpenAlexAuthorDialog from "./drilldowns/RetractionsForOpenAlexAuthorDialog";


interface InstitutionAnnualMetrics {
    id: string,
    institution: string,
    publicationYear: number,
    totalAnalysed: number,
    retractions: number,
    numberOfHighRiskArticles: number,
    numberOfMediumRiskArticles: number,
    numberOfLowRiskArticles: number
}


interface RetractionsByRetractionDateAndPublicationDate {
    retractionYear: number,
    publicationYear: number,
    retractions: number
}

interface InstitutionDashboardProps {
    institutionId: string,
    institutionalRole: string,
    institutionName: string,
    mode: string
}

function InstitutionDashboard({ institutionId, institutionalRole, institutionName, mode }: InstitutionDashboardProps) {
    const START_YEAR = 2014;
    const END_YEAR = 2025;
    const [totalAnalized, setTotalAnalized] = useState<number>(0)
    const [totalRetractions, setTotalRetractions] = useState<number>(0)
    const [highRiskArticles, setHighRiskArticles] = useState<number>(0)
    const [mediumRiskArticles, setMediumRiskArticles] = useState<number>(0)

    const [areInstitutionAnnualMetricsLoading, setAreInstitutionAnnualMetricsLoading] = useState(true)
    const [institutionalDashboardPublicationHistory, setInstitutionalDashboardPublicationHistory] = useState<{ publicationYear: number, retractions: number }[]>([]);
    const [areRetractionsByRetractionDateAndPublicationDateLoading, setAreRetractionsByRetractionDateAndPublicationDateLoading] = useState(true)
    const [retractionsByRetractionDateAndPublicationDate, setRetractionsByRetractionDateAndPublicationDate] = useState<{ data: number[]; label: string; id: string; stack: string; }[]>([]);
    const [retractionsByRetractionDateAndPublicationDateLabels, setRetractionsByRetractionDateAndPublicationDateLabels] = useState<string[]>([]);
    const [drillDownParams, setDrillDownParams] = useState<DrillDownParams>({ startYear: START_YEAR, endYear: END_YEAR, drillDownTitle: "", queryRetractions: true, scitilityRiskScoreLowerBoundary: 0, scitilityRiskScoreHigherBoundary: 1000, journalIds: undefined })
    const [openDrilldown, setOpenDrilldown] = useState<boolean>(false);
    const [openAuthorDrilldown, setOpenAuthorDrilldown] = useState<boolean>(false);
    const [selectedOpenAlexAuthorId, setSelectedOpenAlexAuthorId] = useState<string>();
    const [authorCollaborationMode, setAuthorCollaborationMode] = useState<boolean>(false);
    const [selectedAuthorDisplayName, setSelectedAuthorDisplayName] = useState<string>();
    const [selectedAuthorInstitutionIdAffiliation, setSelectedAuthorInstitutionIdAffiliation] = useState<string>();
    const [selectedAuthorInstitutionNameAffiliation, setSelectedAuthorInstitutionNameAffiliation] = useState<string>();

    function drilldownAuthorWithAffiliation(openAlexAuthorId: string, authorDisplayName: string, institutionId: string | undefined, institutionName: string | undefined, collaborationMode: boolean) {
        setSelectedOpenAlexAuthorId(openAlexAuthorId)
        setSelectedAuthorDisplayName(authorDisplayName)
        setSelectedAuthorInstitutionIdAffiliation(institutionId)
        setSelectedAuthorInstitutionNameAffiliation(institutionName)
        setAuthorCollaborationMode(collaborationMode)
        setOpenAuthorDrilldown(true)
    }

    async function showHighAndMediumRisk(institutionId: string, institutionName: string, journalIds: string | undefined) {

        setDrillDownParams({
            startYear: START_YEAR,
            endYear: END_YEAR,
            drillDownTitle: "High and Medium Risk Articles for " + institutionName + " between " + START_YEAR + " and " + END_YEAR,
            queryRetractions: false,
            institutionId: institutionId,
            scitilityRiskScoreLowerBoundary: 0.5,
            journalIds: journalIds
        })
        setOpenDrilldown(true)

    }

    async function showHighRisk(institutionId: string, institutionName: string) {
        setDrillDownParams({
            startYear: START_YEAR,
            endYear: END_YEAR,
            drillDownTitle: "High Risk Articles for " + institutionName + " between " + START_YEAR + " and " + END_YEAR,
            queryRetractions: false,
            institutionId: institutionId,
            scitilityRiskScoreLowerBoundary: 1.5,
            journalIds: undefined
        })
        setOpenDrilldown(true)

    }

    async function showMediumRisk(institutionId: string, institutionName: string) {

        setDrillDownParams({
            startYear: START_YEAR,
            endYear: END_YEAR,
            drillDownTitle: "Medium Risk Articles for " + institutionName + " between " + START_YEAR + " and " + END_YEAR,
            queryRetractions: false,
            institutionId: institutionId,
            scitilityRiskScoreLowerBoundary: 0.5,
            scitilityRiskScoreHigherBoundary: 1.5,
            journalIds: undefined
        })
        setOpenDrilldown(true)

    }

    const handleDrilldownClose = () => {
        setOpenDrilldown(false);
    };

    async function showRetractions(institutionId: string, institutionName: string, journalIds: string | undefined) {

        setDrillDownParams({
            startYear: START_YEAR,
            endYear: END_YEAR,
            drillDownTitle: "Retractions for " + institutionName + " between " + START_YEAR + " and " + END_YEAR,
            queryRetractions: true,
            institutionId: institutionId,
            scitilityRiskScoreLowerBoundary: 0,
            journalIds: journalIds
        })
        setOpenDrilldown(true)

    }

    useEffect(() => {

        async function fetchData() {
            let authToken = await auth.currentUser?.getIdToken(true)

            fetch(argosApiEntryPoint + '/stats/institutional-summary?institutionId=' + institutionId
                , {
                    method: 'GET',
                    headers: {
                        'content-type': 'application/json;charset=UTF-8',
                        'Authorization': 'Bearer ' + authToken
                    }
                })
                .then((response) => response.json())
                .then((data: InstitutionAnnualMetrics[]) => {
                    setInstitutionalDashboardPublicationHistory(data)
                    let tRetractions = data.reduce((previousValue, currentValue) => previousValue + currentValue.retractions, 0)
                    let tArticles = data.reduce((previousValue, currentValue) => previousValue + currentValue.totalAnalysed, 0)
                    setTotalRetractions(tRetractions);
                    setTotalAnalized(tArticles)
                    setHighRiskArticles(data.reduce((previousValue, currentValue) => previousValue + currentValue.numberOfHighRiskArticles, 0));
                    setMediumRiskArticles(data.reduce((previousValue, currentValue) => previousValue + currentValue.numberOfMediumRiskArticles, 0));
                    setAreInstitutionAnnualMetricsLoading(false)
                })

            fetch(argosApiEntryPoint + '/stats/institutional-summary/retractions-by-retraction-date-and-publication-date?institutionId=' + institutionId
                , {
                    method: 'GET',
                    headers: {
                        'content-type': 'application/json;charset=UTF-8',
                        'Authorization': 'Bearer ' + authToken
                    }
                })
                .then((response) => response.json())
                .then((inputData: RetractionsByRetractionDateAndPublicationDate[]) => {

                    // Step 1: Get continuous retractionYear range
                    const allRetractionYears = inputData.map(d => d.retractionYear);
                    const minRetYear = Math.min(...allRetractionYears);
                    const maxRetYear = Math.max(...allRetractionYears);
                    const retractionYears = Array.from(
                        { length: maxRetYear - minRetYear + 1 },
                        (_, i) => minRetYear + i
                    );

                    // Step 2: Get unique (actual) publication years from data
                    const publicationYears = Array.from(
                        new Set(inputData.map(d => d.publicationYear))
                    ).sort((a, b) => a - b);

                    // Step 3: Build lookup map for quick access
                    const lookup = new Map<string, number>();
                    inputData.forEach(({ retractionYear, publicationYear, retractions }) => {
                        const key = `${publicationYear}_${retractionYear}`;
                        lookup.set(key, retractions);
                    });

                    // Step 4: Build dataset per publication year
                    const retractionsDataset = publicationYears.map(pubYear => {
                        const data = retractionYears.map(retYear => {
                            const key = `${pubYear}_${retYear}`;
                            return lookup.get(key) ?? 0; // 0 if missing
                        });

                        return {
                            data,
                            label: pubYear.toString(),
                            id: `${pubYear}_retractions`,
                            stack: 'retractions',
                        };
                    });

                    // Step 5: Build X-axis label (string version of retractionYears)
                    const retractionDateLabel = retractionYears.map(year => year.toString());

                    setRetractionsByRetractionDateAndPublicationDate(retractionsDataset);
                    setRetractionsByRetractionDateAndPublicationDateLabels(retractionDateLabel);
                    setAreRetractionsByRetractionDateAndPublicationDateLoading(false)
                })
        }
        fetchData()
    }, []);

    const yearValueFormatter = (value: number | null) => `${value}`;

    const chartSetting = {
        yAxis: [
            {
                label: 'Number of articles',
            },
        ],
        //width: '80%',        
        height: 600,
        sx: {
            [`.${axisClasses.left} .${axisClasses.label}`]: {
                transform: 'translate(-10px, 0)',
            },
            ml: 10,
            mr: 10
        },
    };

    const stackedRetractionsChartSetting = {
        yAxis: [
            {
                label: 'Number of retractions (per Publication Year)',
            },
        ],
        height: 600,
        sx: {
            [`.${axisClasses.left} .${axisClasses.label}`]: {
                transform: 'translate(-10px, 0)',
            },
            ml: 10,
            mr: 10
        },
    };

    return <>
        {institutionName &&
            <>
                <Grid container paddingTop={4}>
                    <Grid container spacing={4}>
                        <Grid item xs={12} sm={6} md={4}>
                            <Item elevation={4}>
                                <Typography variant="h5" gutterBottom>Retractions since 2014</Typography>
                                {areInstitutionAnnualMetricsLoading && <CircularProgress />}
                                {!areInstitutionAnnualMetricsLoading && <>
                                    {mode === 'simplified' &&
                                        <Typography variant="h2" gutterBottom>{totalRetractions}</Typography>
                                    }
                                    {mode !== 'simplified' &&
                                        <Button onClick={() => showRetractions(institutionId, institutionName, undefined)}>
                                            <Typography variant="h2" gutterBottom>{totalRetractions}</Typography>
                                        </Button>
                                    }
                                    <Typography>The institution retraction ratio is {percentage(totalRetractions, totalAnalized)}%. Based on the volume of articles {totalAnalized} and the average retraction ratio of 0.1078%, we would have expected {round(totalAnalized * 0.001078)} retractions.</Typography>
                                </>}
                            </Item>
                        </Grid>
                        <Grid item xs={12} sm={6} md={4}>
                            <Item elevation={4}>
                                <Typography variant="h5" gutterBottom>High Risk Articles since 2014</Typography>
                                {areInstitutionAnnualMetricsLoading && <CircularProgress />}
                                {!areInstitutionAnnualMetricsLoading && <>
                                    {mode === 'simplified' &&
                                        <Typography variant="h2" gutterBottom>{highRiskArticles}</Typography>
                                    }
                                    {mode !== 'simplified' &&
                                        <Button onClick={() => showHighRisk(institutionId, institutionName)}>
                                            <Typography variant="h2" gutterBottom>{highRiskArticles}</Typography>
                                        </Button>
                                    }
                                    <Typography>The institution high risk ratio is {percentage(highRiskArticles, totalAnalized)}%. Based on the volume of articles {totalAnalized} and the average high risk ratio of 0.1507%, we would have expected {round(totalAnalized * 0.001507)} high risk articles.</Typography>
                                </>}
                            </Item>
                        </Grid>
                        <Grid item xs={12} sm={6} md={4}>
                            <Item elevation={4}>
                                <Typography variant="h5" gutterBottom>Medium Risk Articles since 2014</Typography>
                                {areInstitutionAnnualMetricsLoading && <CircularProgress />}
                                {!areInstitutionAnnualMetricsLoading && <>
                                    {mode === 'simplified' &&
                                        <Typography variant="h2" gutterBottom>{mediumRiskArticles}</Typography>
                                    }
                                    {mode !== 'simplified' &&
                                        <Button onClick={() => showMediumRisk(institutionId, institutionName)}>
                                            <Typography variant="h2" gutterBottom>{mediumRiskArticles}</Typography>
                                        </Button>
                                    }
                                    <Typography>The institution medium risk ratio is  {percentage(mediumRiskArticles, totalAnalized)}%. Based on the volume of articles {totalAnalized} and the average medium risk ratio of 0.5572%, we would have expected {round(totalAnalized * 0.005572)} medium risk articles.</Typography>
                                </>}
                            </Item>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <Item elevation={4}>
                                <Typography variant="h5" sx={{ mb: 1 }}>
                                    Publication history showing the article classification
                                </Typography>
                                {areInstitutionAnnualMetricsLoading && <CircularProgress />}
                                {!areInstitutionAnnualMetricsLoading && <>
                                    <BarChart
                                        dataset={institutionalDashboardPublicationHistory}
                                        xAxis={[{
                                            label: 'Publication Year',
                                            scaleType: 'band', dataKey: 'publicationYear', valueFormatter: yearValueFormatter
                                        }]}
                                        series={[
                                            { dataKey: 'numberOfLowRiskArticles', stack: 'history', label: 'Low Risk Articles', color: '#1565C0' },
                                            { dataKey: 'numberOfMediumRiskArticles', stack: 'history', label: 'Medium Risk Articles', color: '#ff6700' },
                                            { dataKey: 'numberOfHighRiskArticles', stack: 'history', label: 'High Risk Articles', color: 'red' },
                                            { dataKey: 'retractions', stack: 'history', label: 'Retractions (by publication year)', color: '#8B0000' }
                                        ]}
                                        {...chartSetting}
                                    /></>}
                            </Item>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <Item elevation={4}>
                                <Typography variant="h5" sx={{ mb: 1 }}>
                                    Count of retractions in the year it was retracted
                                </Typography>
                                {areRetractionsByRetractionDateAndPublicationDateLoading && <CircularProgress />}
                                {!areRetractionsByRetractionDateAndPublicationDateLoading && <>
                                    <BarChart
                                        xAxis={[{
                                            data: retractionsByRetractionDateAndPublicationDateLabels,
                                            scaleType: 'band',
                                            label: 'Year of Retraction',
                                            colorMap: {
                                                type: 'ordinal',
                                                values: retractionsByRetractionDateAndPublicationDateLabels,
                                                colors: ["#003366"],
                                                unknownColor: "#003366"
                                            },

                                        }]}
                                        legend={{ hidden: true }}
                                        series={retractionsByRetractionDateAndPublicationDate
                                        }
                                        {...stackedRetractionsChartSetting}
                                    /></>}
                            </Item>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <Item elevation={4}>
                                <Typography variant="h5" sx={{ mb: 1 }}>
                                    Retracted articles by journal
                                </Typography>
                                <InstitutionSummaryJournalGraph institutionName={institutionName} institutionId={institutionId} mode="retractions"
                                    onBarGraphClick={(institutionalId, drilldownTitle, journalId) => {
                                        if (mode !== 'simplified') {
                                            showRetractions(institutionalId, drilldownTitle, journalId.replaceAll('https://openalex.org/', ''))
                                        }
                                    }
                                    }
                                />
                            </Item>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <Item elevation={4}>
                                <Typography variant="h5" sx={{ mb: 1 }}>
                                    High and Medium Risk articles by journal
                                </Typography>
                                <InstitutionSummaryJournalGraph institutionName={institutionName} institutionId={institutionId} mode="high-and-medium-risk"
                                    onBarGraphClick={
                                        (institutionalId, drilldownTitle, journalId) => {
                                            if (mode !== 'simplified') {
                                                showHighAndMediumRisk(institutionalId, drilldownTitle, journalId.replaceAll('https://openalex.org/', ''))
                                            }
                                        }}
                                />
                            </Item>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <Item elevation={4}>
                                <Typography variant="h5" sx={{ mb: 1 }}>
                                    Retracted articles by publisher
                                </Typography>
                                <InstitutionSummaryPublisherGraph institutionId={institutionId} mode="retractions" />
                            </Item>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <Item elevation={4}>
                                <Typography variant="h5" sx={{ mb: 1 }}>
                                    High and Medium Risk articles by publisher
                                </Typography>
                                <InstitutionSummaryPublisherGraph institutionId={institutionId} mode="high-and-medium-risk" />
                            </Item>
                        </Grid>
                        {institutionalRole !== "User" &&
                            <>
                                <Grid item xs={12} md={6}>
                                    <Item elevation={4}>
                                        <Typography variant="h5" sx={{ mb: 1 }}>
                                            Most retracted authors since 2014
                                        </Typography>
                                        <MostRetractedAuthorsTable
                                            institutionId={institutionId}
                                            institutionName={institutionName}
                                            onAuthorSelected={drilldownAuthorWithAffiliation} />
                                    </Item>
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <Item elevation={4}>
                                        <Typography variant="h5" sx={{ mb: 1 }}>
                                            Most retracted Collaborators in your Institution since 2014
                                        </Typography>
                                        <MostFrequentCollaboratorsTable institutionId={institutionId} institutionName={institutionName}
                                            onAuthorSelected={drilldownAuthorWithAffiliation}
                                        />
                                    </Item>
                                </Grid>
                                <Grid item xs={12} md={12}>
                                    <Item elevation={4}>
                                        <Typography variant="h5" sx={{ mb: 1 }}>
                                            Collaborating Institutions linked to Retractions since 2014
                                        </Typography>
                                        <CollaboratingInstitutionsTable institutionId={institutionId} mode="retractions" />
                                    </Item>
                                </Grid></>
                        }
                    </Grid>
                </Grid></>
        }
        <Dialog
            fullScreen
            open={openDrilldown}
            onClose={handleDrilldownClose}
        >
            <AppBar sx={{ position: 'relative' }}>
                <Toolbar>
                    <IconButton
                        edge="start"
                        color="inherit"
                        onClick={handleDrilldownClose}
                        aria-label="close"
                    >
                        <CloseIcon />
                    </IconButton>
                    <Typography sx={{ ml: 2, flex: 1 }} variant="h6" component="div">
                        Institution details
                    </Typography>
                    <Button autoFocus color="inherit" onClick={handleDrilldownClose}>
                        Close
                    </Button>
                </Toolbar>
            </AppBar>
            <DrillDownTable
                journalIds={drillDownParams?.journalIds}
                startYear={drillDownParams?.startYear}
                endYear={drillDownParams?.endYear}
                drillDownTitle={drillDownParams?.drillDownTitle}
                queryRetractions={drillDownParams.queryRetractions}
                scitilityRiskScoreLowerBoundary={drillDownParams.scitilityRiskScoreLowerBoundary}
                scitilityRiskScoreHigherBoundary={drillDownParams.scitilityRiskScoreHigherBoundary}
                institutionId={drillDownParams.institutionId}
            />
        </Dialog>
        <RetractionsForOpenAlexAuthorDialog
            openAlexAuthorId={selectedOpenAlexAuthorId!}
            authorDisplayName={selectedAuthorDisplayName!}
            institutionId={selectedAuthorInstitutionIdAffiliation}
            institutionName={selectedAuthorInstitutionNameAffiliation}
            handleClose={() => setOpenAuthorDrilldown(false)}
            openDrilldown={openAuthorDrilldown}
            collaborationMode={authorCollaborationMode}
        />
    </>
}

export default InstitutionDashboard